import React, { useState, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import { X, Download, Share2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - use CDN for reliability
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PdfPreviewModal = ({ 
  pdfPath, 
  pdfFilename, 
  pdfBlob: providedBlob = null, // PDF ya generado en el navegador (p. ej. presupuesto)
  onClose 
}) => {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageWidth, setPageWidth] = useState(null);

  // Calculate page width based on viewport
  useEffect(() => {
    const updateWidth = () => {
      // Use 90% of viewport width, max 1000px for landscape PDF
      const maxWidth = Math.min(window.innerWidth * 0.9, 1000);
      setPageWidth(maxWidth);
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Load PDF blob on mount
  useEffect(() => {
    const loadPdf = async () => {
      if (providedBlob) {
        setPdfBlob(providedBlob);
        setPdfUrl(URL.createObjectURL(providedBlob));
        setLoading(false);
        return;
      }
      if (!pdfPath) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(
          `${API}/pdf/download?pdf_path=${encodeURIComponent(pdfPath)}`,
          { method: 'GET' }
        );
        
        if (!response.ok) {
          throw new Error('Error al cargar PDF');
        }
        
        const blob = await response.blob();
        setPdfBlob(blob);
        
        // Create object URL for react-pdf
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(err.message || 'Error al cargar PDF');
        toast.error('Error al cargar la vista previa');
      } finally {
        setLoading(false);
      }
    };
    
    loadPdf();
    
    // Cleanup object URL on unmount
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfPath, providedBlob]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    setError('Error al renderizar PDF');
  }, []);

  const goToPrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, numPages || 1));
  };

  const handleDownload = () => {
    if (!pdfBlob || !pdfFilename) return;
    
    // Create download link
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = pdfFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('PDF descargado');
  };

  const handleShare = async () => {
    if (!pdfBlob || !pdfFilename) return;
    
    try {
      const file = new File([pdfBlob], pdfFilename, { type: 'application/pdf' });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Generadores ${pdfFilename}`,
        });
      } else {
        // Fallback to download
        handleDownload();
        toast.info('Compartir no disponible, descargando archivo...');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        handleDownload();
      }
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevPage();
      if (e.key === 'ArrowRight') goToNextPage();
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [numPages, onClose]);

  return (
    <div 
      className="fixed inset-0 z-[100] flex flex-col bg-[#060C18]"
      data-testid="pdf-preview-modal"
    >
      {/* Header with page indicator */}
      <div className="bg-[#0A1422] border-b border-[#1E3550] px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-[#E2EAF4] font-medium text-sm truncate max-w-[200px] sm:max-w-none">
            {pdfFilename || 'Vista previa'}
          </span>
        </div>
        
        {numPages && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevPage}
              disabled={currentPage <= 1}
              className="text-[#E2EAF4] hover:bg-[#1E3550] disabled:opacity-30 h-8 w-8"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <span className="text-[#5A7A9A] text-sm min-w-[80px] text-center">
              {currentPage} / {numPages}
            </span>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNextPage}
              disabled={currentPage >= numPages}
              className="text-[#E2EAF4] hover:bg-[#1E3550] disabled:opacity-30 h-8 w-8"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        )}
        
        <div className="w-8" /> {/* Spacer for balance */}
      </div>

      {/* PDF Content Area */}
      <div className="flex-1 overflow-auto flex items-start justify-center py-4 px-2">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <Loader2 className="w-12 h-12 text-[#FF6B00] animate-spin" />
            <span className="text-[#5A7A9A]">Cargando vista previa...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <div className="text-red-500 text-center">
              <p className="font-medium">{error}</p>
              <p className="text-sm text-[#5A7A9A] mt-2">
                Intenta descargar el PDF directamente
              </p>
            </div>
            <Button
              onClick={handleDownload}
              className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar PDF
            </Button>
          </div>
        ) : pdfUrl ? (
          <Document
            file={pdfUrl}
            key={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-[#FF6B00] animate-spin" />
              </div>
            }
            className="pdf-document"
          >
            <Page
              pageNumber={currentPage}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-6 h-6 text-[#5A7A9A] animate-spin" />
                </div>
              }
              className="pdf-page shadow-2xl rounded-lg overflow-hidden"
            />
          </Document>
        ) : null}
      </div>

      {/* Bottom Action Bar */}
      <div className="bg-[#0A1422] border-t border-[#1E3550] px-4 py-4 flex-shrink-0">
        <div className="flex items-center justify-center gap-3 max-w-lg mx-auto">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 h-12 border-[#1E3550] text-[#E2EAF4] hover:bg-[#1E3550] font-semibold"
            data-testid="pdf-close-btn"
          >
            <X className="w-5 h-5 mr-2" />
            Cerrar
          </Button>
          
          <Button
            onClick={handleDownload}
            disabled={!pdfBlob}
            className="flex-1 h-12 bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold"
            data-testid="pdf-download-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Descargar
          </Button>
          
          <Button
            onClick={handleShare}
            disabled={!pdfBlob}
            variant="outline"
            className="flex-1 h-12 border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF]/10 font-semibold"
            data-testid="pdf-share-btn"
          >
            <Share2 className="w-5 h-5 mr-2" />
            Compartir
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal;
